import type { AdminUser } from '@/entities/admin';
import { adminApi } from '@/features/admin';
import { cn, getStorageUrl } from '@/shared/lib';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Mail,
  Phone,
  Search,
  XCircle,
} from 'lucide-react';
import { type KeyboardEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const PAGE_SIZE = 20;

const searchTypes = [
  { value: '0', label: 'Recherche libre' },
  { value: '1', label: "Inscrits aujourd'hui" },
  { value: '2', label: 'Inscrits cette semaine' },
  { value: '3', label: 'Sans photo / Non valide' },
  { value: '4', label: 'Avec photo' },
  { value: '5', label: 'Injoignables' },
  { value: '6', label: 'Actifs recemment' },
];

const genderOptions = [
  { value: '0', label: 'Tous' },
  { value: '1', label: 'Hommes' },
  { value: '2', label: 'Femmes' },
];

type SortKey = 'username' | 'email' | 'nodeType' | 'isPhotoVerified' | 'createdAt' | 'lastActivity';
type Order = 'asc' | 'desc';

const sortOptions: { value: SortKey; label: string }[] = [
  { value: 'createdAt', label: 'Inscription' },
  { value: 'lastActivity', label: 'Activité' },
  { value: 'username', label: 'Pseudo' },
  { value: 'email', label: 'Email' },
  { value: 'nodeType', label: 'Statut' },
  { value: 'isPhotoVerified', label: 'Photo' },
];

function relative(date?: string, fallback = 'N/A') {
  return date ? formatDistanceToNow(new Date(date), { addSuffix: true, locale: fr }) : fallback;
}

function SortableHead({
  label,
  column,
  sort,
  order,
  onSort,
  className,
}: {
  label: string;
  column: SortKey;
  sort: SortKey | null;
  order: Order;
  onSort: (column: SortKey) => void;
  className?: string;
}) {
  const active = sort === column;
  const Icon = !active ? ArrowUpDown : order === 'asc' ? ArrowUp : ArrowDown;
  return (
    <TableHead
      className={className}
      aria-sort={active ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className={cn(
          'inline-flex items-center gap-1 hover:text-foreground',
          active && 'font-semibold text-foreground',
        )}
      >
        {label}
        <Icon className="h-3.5 w-3.5" />
      </button>
    </TableHead>
  );
}

function PhotoBadge({ verified }: { verified: boolean }) {
  return verified ? (
    <Badge className="bg-success text-white">
      <CheckCircle className="mr-1 h-3 w-3" />
      Validée
    </Badge>
  ) : (
    <Badge variant="destructive">
      <XCircle className="mr-1 h-3 w-3" />
      Non validée
    </Badge>
  );
}

function UserAvatar({ user }: { user: AdminUser }) {
  return (
    <Avatar className="h-10 w-10 shrink-0 rounded-md">
      <AvatarImage src={getStorageUrl(user.photo)} className="object-cover" />
      <AvatarFallback className="rounded-md">
        {user.username?.charAt(0).toUpperCase() || 'U'}
      </AvatarFallback>
    </Avatar>
  );
}

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const [queryInput, setQueryInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('0');
  const [genderId, setGenderId] = useState('0');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortKey | null>(null);
  const [order, setOrder] = useState<Order>('desc');

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ['admin-users', searchQuery, searchType, genderId, page, sort, order],
    queryFn: () =>
      adminApi.searchUsers({
        query: searchQuery || undefined,
        type: searchType !== '0' ? Number.parseInt(searchType) : undefined,
        genderId: genderId !== '0' ? Number.parseInt(genderId) : undefined,
        page,
        limit: PAGE_SIZE,
        sort: sort ?? undefined,
        order,
      }),
    placeholderData: keepPreviousData,
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  const handleSearch = () => {
    setPage(1);
    setSearchQuery(queryInput.trim());
  };

  const handleSort = (column: SortKey) => {
    if (sort === column) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(column);
      setOrder(column === 'username' || column === 'email' ? 'asc' : 'desc');
    }
    setPage(1);
  };

  const openUser = (user: AdminUser) => navigate(`/admin/users/${user.nodeId}`);
  const onRowKey = (e: KeyboardEvent, user: AdminUser) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openUser(user);
    }
  };

  const reset = () => {
    setQueryInput('');
    setSearchQuery('');
    setSearchType('0');
    setGenderId('0');
    setSort(null);
    setOrder('desc');
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold sm:text-3xl">Utilisateurs</h1>
        <Badge variant="outline">{data?.total ?? 0} utilisateur(s)</Badge>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="relative sm:col-span-2">
              <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Pseudo, email, téléphone, #id…"
                value={queryInput}
                onChange={(e) => setQueryInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Select
              value={searchType}
              onValueChange={(v) => {
                setSearchType(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Type de recherche" />
              </SelectTrigger>
              <SelectContent>
                {searchTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={genderId}
              onValueChange={(v) => {
                setGenderId(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Genre" />
              </SelectTrigger>
              <SelectContent>
                {genderOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={handleSearch}>
              <Search className="mr-2 h-4 w-4" />
              Rechercher
            </Button>
            <Button variant="outline" onClick={reset}>
              Réinitialiser
            </Button>
            {/* Sorting for the mobile card list (the table headers sort on desktop) */}
            <div className="flex gap-2 md:hidden">
              <Select
                value={sort ?? 'createdAt'}
                onValueChange={(v) => {
                  setSort(v as SortKey);
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-36" aria-label="Trier par">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sortOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="icon"
                aria-label="Inverser l'ordre"
                onClick={() => setOrder((o) => (o === 'asc' ? 'desc' : 'asc'))}
              >
                {order === 'asc' ? (
                  <ArrowUp className="h-4 w-4" />
                ) : (
                  <ArrowDown className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {isError ? (
        <Card>
          <CardContent className="flex items-center gap-3 p-6 text-destructive">
            <AlertCircle className="h-5 w-5" />
            Impossible de charger les utilisateurs.
          </CardContent>
        </Card>
      ) : isLoading ? (
        <Card>
          <CardContent className="space-y-4 p-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-10 w-10 rounded-md" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-1/4" />
                  <Skeleton className="h-3 w-1/3" />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : data?.users.length ? (
        <div className={cn('transition-opacity', isFetching && 'opacity-60')}>
          {/* Desktop / tablet: sortable table, whole row clickable */}
          <Card className="hidden overflow-hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHead
                    label="Utilisateur"
                    column="username"
                    sort={sort}
                    order={order}
                    onSort={handleSort}
                  />
                  <SortableHead
                    label="Contact"
                    column="email"
                    sort={sort}
                    order={order}
                    onSort={handleSort}
                    className="hidden lg:table-cell"
                  />
                  <SortableHead
                    label="Statut"
                    column="nodeType"
                    sort={sort}
                    order={order}
                    onSort={handleSort}
                  />
                  <SortableHead
                    label="Photo"
                    column="isPhotoVerified"
                    sort={sort}
                    order={order}
                    onSort={handleSort}
                  />
                  <SortableHead
                    label="Inscription"
                    column="createdAt"
                    sort={sort}
                    order={order}
                    onSort={handleSort}
                  />
                  <SortableHead
                    label="Activité"
                    column="lastActivity"
                    sort={sort}
                    order={order}
                    onSort={handleSort}
                    className="hidden xl:table-cell"
                  />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.users.map((user) => (
                  <TableRow
                    key={user.nodeId}
                    tabIndex={0}
                    aria-label={`Ouvrir ${user.username}`}
                    data-testid="admin-user-row"
                    onClick={() => openUser(user)}
                    onKeyDown={(e) => onRowKey(e, user)}
                    className="cursor-pointer focus-visible:bg-muted focus-visible:outline-none"
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <UserAvatar user={user} />
                        <div className="min-w-0">
                          <div className="truncate font-medium">{user.username}</div>
                          <div className="text-sm text-muted-foreground">
                            #{user.nodeId} · {user.gender === 1 ? 'Homme' : 'Femme'}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-1">
                          <Mail className="h-3 w-3 shrink-0" />
                          <span className="truncate">{user.email}</span>
                        </div>
                        {user.phone && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Phone className="h-3 w-3 shrink-0" />
                            {user.phone}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col items-start gap-1">
                        <Badge
                          variant={user.nodeState === 1 ? 'default' : 'destructive'}
                          className="text-xs"
                        >
                          {user.nodeStateLabel}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {user.nodeTypeLabel}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <PhotoBadge verified={user.isPhotoVerified} />
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {relative(user.createdAt)}
                    </TableCell>
                    <TableCell className="hidden text-sm whitespace-nowrap text-muted-foreground xl:table-cell">
                      {relative(user.lastActivity, 'Jamais')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {/* Mobile: cards, whole card clickable */}
          <div className="space-y-3 md:hidden">
            {data.users.map((user) => (
              <button
                key={user.nodeId}
                type="button"
                data-testid="admin-user-card"
                onClick={() => openUser(user)}
                className="w-full rounded-xl border bg-card p-4 text-left shadow-sm transition-colors hover:bg-muted/50"
              >
                <div className="flex items-center gap-3">
                  <UserAvatar user={user} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{user.username}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      #{user.nodeId} · {user.gender === 1 ? 'Homme' : 'Femme'} · {user.email}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Badge
                    variant={user.nodeState === 1 ? 'default' : 'destructive'}
                    className="text-xs"
                  >
                    {user.nodeStateLabel}
                  </Badge>
                  <PhotoBadge verified={user.isPhotoVerified} />
                  <span className="text-xs text-muted-foreground">
                    Inscrit {relative(user.createdAt)}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            Aucun utilisateur trouvé
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="icon"
            aria-label="Page précédente"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="px-3 text-sm">
            Page {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="icon"
            aria-label="Page suivante"
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
